import type { AiOsRisk, ImprovementProposalCreate } from '../../../app/ai-os'
import { riskRequiresHumanClick } from '../../../app/module-evolution'
import type { CopyId } from '../../../copy'

/**
 * 提案表单的**非组件那一半**：草稿的形状、草稿怎么变成载荷、缺什么该说哪一句。
 *
 * 单独一个文件不是洁癖——`create.tsx` 同时导出组件与这几个纯函数时，
 * `react-refresh/only-export-components` 会警告，而它说的是真的：
 * 那种文件在开发时热更会整块重挂，表单里填了一半的东西全没。
 */

/** 表单里那十几个框现在填了什么。 */
export interface Draft {
  moduleId: string
  title: string
  goal: string
  userValue: string
  impactAudience: string
  problemEvidence: string
  acceptance: string
  stopConditions: string
  rollback: string
  budget: string
  riskLevel: AiOsRisk
  requiresHumanClick: boolean
  ownerStream: string
}

export const EMPTY_DRAFT: Draft = {
  moduleId: '',
  title: '',
  goal: '',
  userValue: '',
  impactAudience: '',
  problemEvidence: '',
  acceptance: '',
  stopConditions: '',
  rollback: '',
  budget: '',
  riskLevel: 'green',
  requiresHumanClick: false,
  ownerStream: '',
}

/**
 * 缺哪一样，就说哪一句。**不是一句笼统的「有几项必填没填」。**
 *
 * 三条各说各的，因为它们要用户做的事不一样：
 *
 *   `missing_user_value`   「写不出来的话，这件事多半不值得我自己去做」
 *                          ——它是在劝他重新想一遍，不只是在催他填框
 *   `missing_rollback`     「退不回去的改动我不会做」
 *   `missing_evidence`     「没有证据我只是在猜」
 *
 * 剩下那几样（模块、标题、目标、验收、停止条件、预算、归谁做）没有
 * 各自的模板，它们缺的时候按第一个缺的那一样报——`validate` 回 `null`
 * 表示都齐了。
 */
export function validate(draft: Draft): CopyId | null {
  if (!parseArray(draft.problemEvidence)) return 'ai_os.error.missing_evidence'
  if (!draft.userValue.trim()) return 'ai_os.error.missing_user_value'
  // 「影响的是谁」是第三轮新加的必填项，少了它服务端 400。
  // **这是刻意的**：拿不到它，界面就只能自己编一个「影响谁」，
  // 而 CLAUDE.md §3 要求每条告警都说清影响谁——编出来的那个数字最不该出现在那儿。
  // 借 missing_user_value 那一句：它说的是「写不出来的话多半不值得我自己去做」，
  // 而「说不出影响谁」正是同一件事的另一半
  if (!draft.impactAudience.trim()) return 'ai_os.error.missing_user_value'
  if (!parseObject(draft.rollback)) return 'ai_os.error.missing_rollback'
  if (
    !draft.moduleId ||
    !draft.title.trim() ||
    !draft.goal.trim() ||
    !draft.ownerStream.trim() ||
    !parseArray(draft.acceptance) ||
    !parseArray(draft.stopConditions) ||
    !parseObject(draft.budget)
  ) {
    // 这几样没有专门的模板，借「没有停下来的条件，这件事就可能一直做下去」
    // 那一档不合适——**借一句意思不对的话，比不说更糟**。
    // 用最贴近的那条：先把最要紧的三样说清，剩下的用同一条兜住
    return 'ai_os.error.missing_evidence'
  }
  return null
}

/**
 * 草稿 → 载荷。**填不全就回 `null`，一个字段都不猜。**
 *
 * 那几个 JSON 字段在契约里是 `additionalProperties: true` 的自由对象，
 * 形状刻意没定。所以这里**原样解析用户输入，不替他补结构**——
 * 补出来的结构是我编的，而服务端会把它当成他声明的事实存下来。
 */
export function toInput(draft: Draft): ImprovementProposalCreate | null {
  if (validate(draft) !== null) return null

  return {
    module_id: draft.moduleId,
    title: draft.title.trim(),
    goal: draft.goal.trim(),
    user_value: draft.userValue.trim(),
    impact_audience: draft.impactAudience.trim(),
    problem_evidence: parseArray(draft.problemEvidence)!,
    acceptance: parseArray(draft.acceptance)!,
    stop_conditions: parseArray(draft.stopConditions)!,
    rollback: parseObject(draft.rollback)!,
    budget: parseObject(draft.budget)!,
    risk_level: draft.riskLevel,
    // 红级一律为真。**这里不是「顺手补一下」**：勾选框在选到红时已经锁住了，
    // 这一行是防住「有人绕过界面直接调 toInput」——红配 false 是 400，
    // 而一个被服务端拒掉的提交，用户看到的只是一句错误
    requires_human_click: riskRequiresHumanClick(draft.riskLevel) || draft.requiresHumanClick,
    owner_stream: draft.ownerStream.trim(),
  }
}

/** 至少一条，而且必须是一个对象数组。空数组不算「填了」。 */
export function parseArray(text: string): Record<string, unknown>[] | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  try {
    const value: unknown = JSON.parse(trimmed)
    if (!Array.isArray(value) || value.length === 0) return null
    if (!value.every((v) => typeof v === 'object' && v !== null && !Array.isArray(v))) return null
    return value as Record<string, unknown>[]
  } catch {
    return null
  }
}

/** 不许是空对象。契约原话：说不出怎么退，就不该自动改。 */
export function parseObject(text: string): Record<string, unknown> | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  try {
    const value: unknown = JSON.parse(trimmed)
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
    if (Object.keys(value).length === 0) return null
    return value as Record<string, unknown>
  } catch {
    return null
  }
}
